import React, { useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import WalletConnect from './WalletConnect';

const AirdropHistoryPage = () => {
    const [histories, setHistories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [walletAddress, setWalletAddress] = useState('');
    const [manualAddress, setManualAddress] = useState('');
    const [addressInputMethod, setAddressInputMethod] = useState('wallet'); // 'wallet' 或 'manual'

    // 修改 handleWalletAddressReceived 函数，避免重复调用
    const handleWalletAddressReceived = (address) => {
        if (addressInputMethod === 'wallet' && address !== walletAddress) {
            setWalletAddress(address);
            if (address) {
                fetchHistory(address);
            } else {
                // 如果没有地址（钱包断开连接），清空历史记录
                setHistories([]);
            }
        }
    };

    const handleManualAddressChange = (e) => {
        setManualAddress(e.target.value);
    };

    const handleInputMethodChange = (method) => {
        setAddressInputMethod(method);
        // 切换时清空历史记录
        setHistories([]);
        setError('');
    };

    const handleManualSearch = () => {
        if (manualAddress.trim()) {
            // 验证BTC地址格式（简单验证）
            if (!/^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,62}$/.test(manualAddress)) {
                setError('请输入有效的BTC地址');
                return;
            }
            fetchHistory(manualAddress);
        } else {
            setError('请输入BTC地址');
        }
    };

    const fetchHistory = async (address) => {
        if (loading) return;

        try {
            setLoading(true);
            setError('');
            const response = await axios.get('/backend/api/airdrop/history?address=' + address);
            setHistories(response.data.data);
        } catch (err) {
            setError('获取历史记录失败，请稍后重试');
            console.error('History fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    // 格式化日期
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // 格式化地址以显示缩略形式
    const formatAddress = (address) => {
        if (!address) return '';
        return `${address.substring(0, 8)}...${address.substring(address.length - 8)}`;
    };

    // 确定是否应该显示空状态
    const shouldShowEmptyState = () => {
        if (addressInputMethod === 'wallet') {
            return !walletAddress;
        } else {
            return !manualAddress.trim() && histories.length === 0;
        }
    };

    return (
        <PageContainer>
            <PageTitle>空投历史记录</PageTitle>

            <AddressMethodSelector>
                <MethodButton
                    active={addressInputMethod === 'wallet'}
                    onClick={() => handleInputMethodChange('wallet')}
                >
                    使用OKX钱包
                </MethodButton>
                <MethodButton
                    active={addressInputMethod === 'manual'}
                    onClick={() => handleInputMethodChange('manual')}
                >
                    手动输入地址
                </MethodButton>
            </AddressMethodSelector>

            {addressInputMethod === 'wallet' ? (
                <WalletConnectWrapper>
                    <WalletConnect onAddressReceived={handleWalletAddressReceived} />
                </WalletConnectWrapper>
            ) : (
                <ManualAddressContainer>
                    <ManualAddressInput
                        type="text"
                        value={manualAddress}
                        onChange={handleManualAddressChange}
                        placeholder="请输入您的BTC地址"
                    />
                    <SearchButton onClick={handleManualSearch}>查询</SearchButton>
                </ManualAddressContainer>
            )}

            {loading ? (
                <LoadingMessage>加载历史记录中...</LoadingMessage>
            ) : shouldShowEmptyState() ? (
                <EmptyStateContainer>
                    <EmptyStateText>
                        {addressInputMethod === 'wallet'
                            ? '请先连接 OKX 钱包查看您的空投历史记录'
                            : '请输入BTC地址查看您的空投历史记录'}
                    </EmptyStateText>
                    <ReturnButton onClick={() => window.location.href = '/'}>返回下单页面</ReturnButton>
                </EmptyStateContainer>
            ) : error ? (
                <ErrorMessage>{error}</ErrorMessage>
            ) : histories.length === 0 ? (
                <EmptyStateContainer>
                    <EmptyStateText>暂无空投历史记录</EmptyStateText>
                    <ReturnButton onClick={() => window.location.href = '/'}>返回下单页面</ReturnButton>
                </EmptyStateContainer>
            ) : (
                <>
                    <HistoryContainer>
                        {histories.map((history) => (
                            <HistoryCard key={history._id}>
                                <HistoryHeader>
                                    <HistoryDate>{formatDate(history.createdAt)}</HistoryDate>
                                    <StatusBadge status={history.status}>
                                        {history.status === 'completed' ? '已完成' : '处理中'}
                                    </StatusBadge>
                                </HistoryHeader>

                                <HistoryContent>
                                    <HistoryItem>
                                        <ItemLabel>铭文名称:</ItemLabel>
                                        <ItemValue>{history.inscriptionName}</ItemValue>
                                    </HistoryItem>

                                    <HistoryItem>
                                        <ItemLabel>空投数量:</ItemLabel>
                                        <ItemValue>{history.addressCount} 个地址</ItemValue>
                                    </HistoryItem>

                                    <HistoryItem>
                                        <ItemLabel>地址类型:</ItemLabel>
                                        <ItemValue>{history.addressType === 'random' ? '随机地址' : '指定地址'}</ItemValue>
                                    </HistoryItem>

                                    {history.addressType !== 'random' && (
                                        <HistoryItem>
                                            <ItemLabel>{history.specifiedDesc}</ItemLabel>
                                        </HistoryItem>
                                    )}

                                    <HistoryItem>
                                        <ItemLabel>下单地址:</ItemLabel>
                                        <ItemValue>
                                            {formatAddress(history.orderAddress)}
                                        </ItemValue>
                                    </HistoryItem>
                                    <HistoryItem>
                                        <ItemLabel>BTC接收地址:</ItemLabel>
                                        <ItemValue>
                                            {history.btcAddress}
                                        </ItemValue>
                                        <CopyButton onClick={() => copyToClipboard(history.btcAddress)}>
                                            复制地址
                                        </CopyButton>
                                    </HistoryItem>
                                    <HistoryItem>
                                        <ItemLabel>BTC接收数量 :</ItemLabel>
                                        <ItemValue>
                                            {history.btcAmount}
                                        </ItemValue>
                                    </HistoryItem>
                                    <HistoryItem>
                                        <ItemLabel>铭文接收地址:</ItemLabel>
                                        <ItemValue>
                                            {history.inscriptionAddress}
                                        </ItemValue>
                                        <CopyButton onClick={() => copyToClipboard(history.inscriptionAddress)}>
                                            复制地址
                                        </CopyButton>
                                    </HistoryItem>
                                    <HistoryItem>
                                        <ItemLabel>铭文接收数量 :</ItemLabel>
                                        <ItemValue>
                                            {history.inscriptionAmount}
                                        </ItemValue>
                                    </HistoryItem>

                                    {history.txId && (
                                        <HistoryItem>
                                            <ItemLabel>付款交易ID:</ItemLabel>
                                            <ItemValue>
                                                {formatAddress(history.txId)}
                                                <ViewButton onClick={() => window.open(`https://mempool.space/tx/${history.txId}`, '_blank')}>
                                                    查看
                                                </ViewButton>
                                            </ItemValue>
                                        </HistoryItem>
                                    )}
                                    {!history.txId && (
                                        <HistoryItem>
                                            <ItemLabel>未付款(存在延迟，付款成功后页面会付款信息过比较久才刷新)</ItemLabel>
                                        </HistoryItem>
                                    )}
                                </HistoryContent>
                            </HistoryCard>
                        ))}
                    </HistoryContainer>

                    <ButtonContainer>
                        <ReturnButton onClick={() => window.location.href = '/'}>返回下单页面</ReturnButton>
                    </ButtonContainer>
                </>
            )}

            <DisclaimerContainer>
                <DisclaimerTitle>免责声明</DisclaimerTitle>
                <DisclaimerText>
                    本服务仅供区块链技术研究和学习使用，不构成任何投资建议。用户使用本服务所产生的任何风险和后果由用户自行承担，平台不承担任何法律责任。铭文资产价值具有高度波动性，请用户自行评估风险。
                </DisclaimerText>
                <RegionRestriction>
                    注意：本服务不面向中国大陆用户开放，中国大陆用户请勿使用本服务。用户须确保自己在使用本服务时遵守当地法律法规。
                </RegionRestriction>
            </DisclaimerContainer>
        </PageContainer>
    );
};

// 辅助函数 - 复制到剪贴板
const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
        .then(() => {
            alert('已复制到剪贴板');
        })
        .catch(err => {
            console.error('复制失败:', err);
        });
};

// 样式组件
const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 20px;
  }
`;

const PageTitle = styled.h1`
  text-align: center;
  margin-bottom: 40px;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 36px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 28px;
    margin-bottom: 30px;
  }
`;

const HistoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 30px;
`;

const HistoryCard = styled.div`
  background-color: rgba(30, 30, 30, 0.6);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-3px);
  }
`;

const HistoryHeader = styled.div`
  background-color: rgba(40, 40, 40, 0.8);
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HistoryDate = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
`;

const StatusBadge = styled.span`
  background-color: ${({ status, theme }) =>
        status === 'completed' ? 'rgba(39, 174, 96, 0.2)' : 'rgba(243, 156, 18, 0.2)'};
  color: ${({ status, theme }) =>
        status === 'completed' ? '#27ae60' : '#f39c12'};
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
`;

const HistoryContent = styled.div`
  padding: 20px;
`;

const HistoryItem = styled.div`
  display: flex;
  margin-bottom: 12px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const ItemLabel = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
  min-width: 100px;
  margin-right: 10px;
`;

const ItemValue = styled.span`
  color: ${({ theme }) => theme.colors.text};
  flex: 1;
  display: flex;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-top: 5px;
  }
`;

const CopyButton = styled.button`
  background-color: transparent;
  color: ${({ theme }) => theme.colors.secondary};
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  margin-left: 10px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: rgba(247, 147, 26, 0.1);
  }
`;

const ViewButton = styled(CopyButton)`
  color: ${({ theme }) => theme.colors.primary};
  border-color: ${({ theme }) => theme.colors.primary};
  
  &:hover {
    background-color: rgba(255, 153, 0, 0.1);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 30px 0;
`;

const ReturnButton = styled.button`
  background-color: transparent;
  color: ${({ theme }) => theme.colors.secondary};
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background-color: rgba(247, 147, 26, 0.1);
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.text};
  padding: 40px;
  font-size: 18px;
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.errorColor};
  background-color: rgba(255, 85, 85, 0.1);
  padding: 15px 20px;
  border-radius: 8px;
  text-align: center;
  margin: 30px 0;
`;

const EmptyStateContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
  background-color: rgba(30, 30, 30, 0.6);
  border-radius: 12px;
`;

const EmptyStateText = styled.p`
  color: ${({ theme }) => theme.colors.text};
  font-size: 18px;
  margin-bottom: 20px;
`;

const DisclaimerContainer = styled.div`
  background-color: rgba(20, 20, 20, 0.7);
  border-radius: 8px;
  padding: 20px;
  margin-top: 40px;
  border-left: 3px solid ${({ theme }) => theme.colors.secondary};
`;

const DisclaimerTitle = styled.h3`
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: 10px;
  font-size: 18px;
`;

const DisclaimerText = styled.p`
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  margin-bottom: 15px;
  line-height: 1.6;
`;

const RegionRestriction = styled.p`
  color: #ff5555;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.6;
`;

// 新增样式组件
const WalletConnectWrapper = styled.div`
  margin-bottom: 30px;
`;

const AddressMethodSelector = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  background-color: rgba(30, 30, 30, 0.6);
  border-radius: 8px;
  padding: 10px;
`;

const MethodButton = styled.button`
  padding: 10px 20px;
  background-color: ${({ active, theme }) =>
        active ? theme.colors.secondary : 'transparent'};
  color: ${({ active }) => active ? '#000' : '#fff'};
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 6px;
  font-size: 14px;
  margin: 0 5px;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background-color: ${({ active, theme }) =>
        active ? theme.colors.secondary : 'rgba(247, 147, 26, 0.2)'};
  }
`;

const ManualAddressContainer = styled.div`
  display: flex;
  margin-bottom: 30px;
  background-color: rgba(30, 30, 30, 0.6);
  border-radius: 8px;
  padding: 15px;
`;

const ManualAddressInput = styled.input`
  flex: 1;
  padding: 12px 15px;
  background-color: rgba(40, 40, 40, 0.8);
  border: 1px solid #333;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.secondary};
  }
  
  &::placeholder {
    color: #777;
  }
`;

const SearchButton = styled.button`
  background-color: ${({ theme }) => theme.colors.secondary};
  border: none;
  border-radius: 8px;
  color: #000;
  font-size: 16px;
  padding: 0 20px;
  margin-left: 10px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #e78c19;
  }
`;

export default AirdropHistoryPage;